import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { API } from "@/App";
import { 
  CreditCard, 
  IndianRupee,
  Calendar,
  FileText,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  Receipt,
  TrendingUp,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SchoolBilling() {
  const { admin, school } = useOutletContext();
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "bank_transfer",
    transaction_id: "",
    reference_number: "",
    notes: ""
  });

  const permissions = admin?.permissions || [];
  const canManage = permissions.includes("manage_billing");
  const canView = permissions.includes("view_billing") || canManage;

  useEffect(() => {
    if (canView) {
      fetchBillingData();
    } else {
      setLoading(false);
    }
  }, [canView]);

  const fetchBillingData = async () => {
    try {
      // Fetch billing summary
      const summaryRes = await fetch(`${API}/school/billing/summary`, {
        credentials: "include",
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setBilling(data);
      }

      // Fetch invoices from new payment routes
      const invoicesRes = await fetch(`${API}/school/payments/invoices`, {
        credentials: "include",
      });
      if (invoicesRes.ok) {
        const data = await invoicesRes.json();
        setInvoices(data.invoices || []);
      }

      // Fetch payment history
      const paymentsRes = await fetch(`${API}/school/payments/history`, {
        credentials: "include",
      });
      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPayments(data.payments || []);
      }

      // Fetch payment summary
      const summaryPayRes = await fetch(`${API}/school/payments/summary`, {
        credentials: "include",
      });
      if (summaryPayRes.ok) {
        const data = await summaryPayRes.json();
        setPaymentSummary(data.summary);
      }
    } catch (error) {
      console.error("Error fetching billing:", error);
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice || !paymentForm.amount) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const response = await fetch(`${API}/school/payments/record`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          invoice_id: selectedInvoice.invoice_id,
          amount: parseFloat(paymentForm.amount),
          payment_method: paymentForm.payment_method,
          transaction_id: paymentForm.transaction_id || null,
          reference_number: paymentForm.reference_number || null,
          notes: paymentForm.notes || null
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message || "Payment recorded");
        setShowRecordPaymentModal(false);
        setSelectedInvoice(null);
        setPaymentForm({
          amount: "",
          payment_method: "bank_transfer",
          transaction_id: "",
          reference_number: "",
          notes: ""
        });
        fetchBillingData();
      } else {
        toast.error(data.error || "Failed to record payment");
      }
    } catch (error) {
      toast.error("Network error");
    }
  };

  const handleExport = async (type) => {
    try {
      let url = "";
      let filename = "";
      
      switch(type) {
        case "invoices":
          url = `${API}/school/reports/invoices/csv`;
          filename = "invoices.csv";
          break;
        case "payments":
          url = `${API}/school/reports/payments/csv`;
          filename = "payments.csv";
          break;
        case "students":
          url = `${API}/school/reports/students/csv`;
          filename = "students.csv";
          break;
        default:
          return;
      }

      const response = await fetch(url, { credentials: "include" });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${type} exported`);
      } else {
        toast.error("Export failed");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case "paid":
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case "partially_paid":
        return <Badge className="bg-blue-100 text-blue-700">Partial</Badge>;
      case "sent":
        return <Badge className="bg-amber-100 text-amber-700">Sent</Badge>;
      case "overdue":
        return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
      case "draft":
        return <Badge className="bg-slate-100 text-slate-700">Draft</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
        <p className="text-slate-500">You don't have permission to view billing information.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  const subscription = school?.billing?.subscription;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Billing & Payments</h1>
          <p className="text-slate-500 text-sm">Manage invoices, payments, and subscriptions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleExport("invoices")}>
            <Download className="w-4 h-4 mr-2" />
            Export Invoices
          </Button>
          <Button variant="outline" onClick={() => handleExport("payments")}>
            <Download className="w-4 h-4 mr-2" />
            Export Payments
          </Button>
        </div>
      </div>

      {/* Payment Summary Stats */}
      {paymentSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(paymentSummary.total_invoiced)}</p>
                  <p className="text-xs text-slate-500">Total Invoiced</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(paymentSummary.total_paid)}</p>
                  <p className="text-xs text-slate-500">Total Paid</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(paymentSummary.total_due)}</p>
                  <p className="text-xs text-slate-500">Outstanding</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-800">{formatCurrency(paymentSummary.overdue_amount)}</p>
                  <p className="text-xs text-slate-500">Overdue ({paymentSummary.overdue_count})</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Subscription Status */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                Subscription Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscription ? (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Plan</p>
                    <p className="text-lg font-semibold text-slate-800 capitalize">
                      {subscription.plan_tier?.replace("school_", "")}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Billing Cycle</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {subscription.cycle?.replace("_", " ")}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Status</p>
                    <Badge className={`${
                      subscription.status === "active" 
                        ? "bg-green-100 text-green-700" 
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {subscription.status === "active" ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Active</>
                      ) : (
                        <><Clock className="w-3 h-3 mr-1" /> {subscription.status}</>
                      )}
                    </Badge>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500 mb-1">Renews On</p>
                    <p className="text-lg font-semibold text-slate-800">
                      {formatDate(subscription.end_date)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">No Active Subscription</h3>
                  <p className="text-slate-500 mb-4">
                    {school?.status === "verified" 
                      ? "Choose a plan to get started"
                      : "School verification pending - billing will be available after verification"
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing Summary */}
          {billing && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <IndianRupee className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {formatCurrency(billing.current_month_amount)}
                      </p>
                      <p className="text-sm text-slate-500">Current Month</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {billing.billable_students || 0}
                      </p>
                      <p className="text-sm text-slate-500">Billable Students</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {formatDate(billing.next_billing_date)}
                      </p>
                      <p className="text-sm text-slate-500">Next Billing</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Invoice History
              </CardTitle>
              <CardDescription>View invoices and record payments</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No invoices yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map((invoice) => (
                    <div 
                      key={invoice.invoice_id || invoice.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      data-testid={`invoice-${invoice.invoice_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{invoice.invoice_number}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{formatDate(invoice.invoice_date || invoice.date)}</span>
                            {invoice.po_number && (
                              <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">PO: {invoice.po_number}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-slate-800">{formatCurrency(invoice.total_amount || invoice.amount)}</p>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(invoice.status)}
                            {invoice.amount_due > 0 && invoice.status !== "paid" && (
                              <span className="text-xs text-red-600">Due: {formatCurrency(invoice.amount_due)}</span>
                            )}
                          </div>
                        </div>
                        {canManage && invoice.amount_due > 0 && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setPaymentForm(prev => ({ ...prev, amount: invoice.amount_due.toString() }));
                              setShowRecordPaymentModal(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Record Payment
                          </Button>
                        )}
                        <Button variant="ghost" size="icon">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-slate-600" />
                Payment History
              </CardTitle>
              <CardDescription>View all recorded payments</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No payments recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div 
                      key={payment.payment_id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{payment.receipt_number}</p>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <span>{formatDate(payment.payment_date)}</span>
                            <span>•</span>
                            <span className="capitalize">{payment.payment_method?.replace("_", " ")}</span>
                            {payment.transaction_id && (
                              <>
                                <span>•</span>
                                <span>Txn: {payment.transaction_id}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-slate-500">Invoice: {payment.invoice_number}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Modal */}
      <Dialog open={showRecordPaymentModal} onOpenChange={setShowRecordPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a payment for invoice {selectedInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleRecordPayment} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount *</label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
              />
              {selectedInvoice && (
                <p className="text-xs text-slate-500 mt-1">Amount due: {formatCurrency(selectedInvoice.amount_due)}</p>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Payment Method</label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Transaction ID</label>
              <Input
                value={paymentForm.transaction_id}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, transaction_id: e.target.value }))}
                placeholder="Transaction or UTR number"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">Reference Number</label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                placeholder="Optional reference"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRecordPaymentModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
